import { useState } from 'react';
import PropTypes from 'prop-types';
import { DownloadOutlined } from '@ant-design/icons';
import { Col, Modal, Tooltip } from 'antd';
import { Table } from 'formik-antd';
import moment from 'moment';
import { Box, Flex, Text } from 'atoms';
import { getDownloadReportList } from 'store/driverSlice';
import { getLocalDate, getLocalTime } from 'utils/helpers';
import { DOWNLOAD_STATUSES } from 'utils/router-static-data';
import { TripStatusOptions } from 'components';

const downloadProgressType = [
	{ id: 'Pending', name: 'Pending', color: '#0482FF', background: '#E6F2FF' },
	{ id: 'In Progress', name: 'In Progress', color: '#FB8500', background: '#FFF3E6' },
	{ id: 'Processed', name: 'Processed', color: '#04B166', background: '#DCFFF0' },
	{ id: 'Failed', name: 'Failed', color: '#EF2F4B', background: '#FFE7EA' },
];
function FilterDetailsColumn({ task_data, min_length = 2 }) {
	const modalData = Object.keys(task_data)
		.filter((key) => !key.includes('_id') && key !== 'login_user_type')
		.reduce(
			(obj, key) =>
				Object.assign(obj, {
					[key]: task_data[key],
				}),
			{}
		);

	const [modalVisible, setModalVisible] = useState(false);
	const keyLabelMap = {
		city: 'City',
		client_name: 'Client Name',
		vendor_name: 'Vendor Name',
		from_date: 'From Date',
		to_date: 'To Date',
		project_location_code: 'Project Location Code',
		trip_type: 'Trip Type',
		trip_status: 'Trip Status',
		is_valid: 'Valid',
		query_str: 'Search',
	};

	const filters = Object.entries(modalData);
	const rows = filters
		.filter((res) => !res.includes('not_selected') && res)
		.map(([keys, values]) => {
			const filterLabel = keyLabelMap[keys] || keys.split('_').join(' ');
			const changeValue = (data, label) => {
				const currentDate = moment.utc(data).local().format('DD/MM/YYYY');
				let fialData = null;
				if (label === 'trip_status') {
					fialData = TripStatusOptions[values - 1].label;
				} else if (label === 'is_valid' && data === true) {
					fialData = 'Valid';
				} else if (label === 'is_valid' && data === false) {
					fialData = 'Invalid';
				} else if (label === 'is_valid' && data === 'All') {
					fialData = 'All';
				} else {
					fialData = currentDate;
				}
				return fialData;
			};

			return {
				key: keys,
				filter: filterLabel,
				value:
					// eslint-disable-next-line no-nested-ternary
					keys === 'is_valid' ||
					keys === 'trip_status' ||
					keys === 'from_date' ||
					keys === 'to_date'
						? changeValue(values, keys)
						: values,
			};
		})
		.filter(Boolean);

	const columns = [
		{ title: 'Filter', dataIndex: 'filter', key: 'filter' },
		{ title: 'Value', dataIndex: 'value', key: 'value' },
	];

	const showFilterDetail = (res) => {
		const finalData = res.filter(
			(data) => data.key !== 'to_date' && data.value !== 'Invalid date'
		);
		const getKeys = [];
		finalData.forEach((res1) =>
			getKeys.push(res1.filter === 'From Date' ? 'Date' : res1.filter)
		);

		if (getKeys.length > min_length) {
			const finalDataWithPlus = [];
			for (let i = 0; i < min_length; i += 1) {
				finalDataWithPlus.push(getKeys[i]);
			}
			return `${finalDataWithPlus.join(', ')} +${res.length - min_length}`;
		}
		return getKeys.join(', ');
	};

	return (
		<>
			<span
				style={{ cursor: 'pointer' }}
				onClick={() => setModalVisible(true)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') setModalVisible(true);
				}}
				role="button"
				tabIndex={0}
			>
				{rows.length > 0 && showFilterDetail(rows)}
			</span>
			<Modal
				title="Filter Details"
				visible={modalVisible}
				onCancel={() => setModalVisible(false)}
				footer={null}
			>
				<Table dataSource={rows} columns={columns} pagination={false} />
			</Modal>
		</>
	);
}

FilterDetailsColumn.propTypes = {
	task_data: PropTypes.object.isRequired,
	min_length: PropTypes.number,
};

export const DownloadReportsTableConfig = [
	{
		title: 'File Name',
		dataIndex: 'filename',
		key: 'filename',
		// width: '350px',
		hidden: false,
		render: (filename) => <Text overFlow="hidden">{filename || ''}</Text>,
	},
	{
		title: 'File Type',
		dataIndex: 'type',
		key: 'type',
		hidden: false,
		width: '180px',
		render: (type) => <Text overFlow="hidden">{type || ''}</Text>,
	},
	{
		title: 'Filter Details',
		dataIndex: 'task_data',
		key: 'task_data',
		hidden: false,
		render: (task_data) => <FilterDetailsColumn task_data={task_data} min_length={3} />,
	},

	{
		title: 'Requested By',
		dataIndex: 'last_name',
		key: 'last_name',
		hidden: false,
		width: '200px',
		render: (last_name, record) => (
			<div>
				<Text overFlow="hidden">{`${record?.first_name} ${last_name}` || ''}</Text>
				<Text overFlow="hidden">
					{getLocalDate(record.created_at)} {getLocalTime(record.created_at)}
				</Text>
			</div>
		),
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		hidden: false,
		width: '10%',
		options: { DOWNLOAD_STATUSES },
		render: (status) => {
			const getStatusName = downloadProgressType.filter((x) => x.id === status);
			const statusName =
				getStatusName.length > 0
					? getStatusName[0]
					: { name: 'not found', color: '#33b5e5', background: '#FFE7FF' };

			return (
				<Flex
					style={{
						background: statusName?.background || '#FFE7FF',
						borderRadius: '6px',
						padding: '8px',
						justifyContent: 'center',
					}}
				>
					<Flex marginRight="5px">
						<Box
							as="span"
							width="10px"
							height="10px"
							borderRadius="50%"
							margin="auto"
							background={statusName?.color || '#FFE7FF'}
						/>
					</Flex>
					<Text
						style={{ whiteSpace: 'normal' }}
						overFlow="hidden"
						fontSize="12px"
						lineHeight="14px"
						color={statusName.color || '#33b5e5'}
					>
						{statusName.name}
					</Text>
				</Flex>
			);
		},
	},
	{
		title: 'Action',
		dataIndex: 'id',
		key: 'id',
		hidden: false,
		width: '100px',
		render: (id, record) => {
			// <ACL action="update" module="driver">
			// 	<StatusAction statusData={record?.is_active} userType="driver" id={id} />
			// </ACL>
			const status = record?.status;
			const getFileName = record?.filename;
			if (status === 'Processed' && getFileName) {
				return (
					<Col>
						<Tooltip title="Download Report">
							<DownloadOutlined
								style={{
									color: '#2196F3',
									fontSize: '20px',
								}}
								onClick={getDownloadReportList({ getFileName })}
							/>
						</Tooltip>
					</Col>
				);
			}
			return null;
		},
	},
];

export default function FilterPopup() {}
